import React, { useState, useEffect } from 'react';
import Panel from 'Components/Panel/Panel';
import ExecuteAxios from 'Components/ExecuteAxios/ExecuteAxios';
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator';
import PanelListDefault from 'Components/PanelListDefault/PanelListDefault';
import ApplyAttributes from 'Components/ApplyAttributes/ApplyAttributes';
import AutomaticallyListedAttributes from 'Components/AutomaticallyListedAttributes/AutomaticallyListedAttributes';
import ResetVariables from 'Components/ResetVariables/ResetVariables';
import RemoveIdsNotFound from 'Components/RemoveIdsNotFound/RemoveIdsNotFound';
import OptionsDataByAttributePath from 'Components/Options/optionsData/OptionsDataByAttributePath';
import FilterOrder from './FilterOrder/FilterOrder';

const ServiceOrder = ({
    serviceOrderIdSelected,
    onUpdateServiceOrderId
}) => {

    const [inserting, setInserting] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [loadingItemPanel, setLoadingItemPanel] = useState(null);
    const [recordsItemByUserId, setRecordsItemByUserId] = useState(null);
    const [attributesDetailItemPanel, setAttributesDetailItemPanel] = useState(null);
    const [clientIdFilter, setClientIdFilter] = useState(null);
    const [isActiveFilter, setIsActiveFilter] = useState('S');

    const readOnlyAttributes = !(inserting || updating);

    const [id, setId] = useState(null);
    const [clientId, setClientId] = useState(null);
    const [vehicleId, setVehicleId] = useState(null);
    const [dateTime, setDateTime] = useState(null);
    const [observation, setObservation] = useState(null);
    const [isActive, setIsActive] = useState('S');
    const [vehicleList, setVehiclesList] = useState(null);
    const [clientList, setClientList] = useState(null);

    useEffect(() => {
        setVehiclesList([])
        const onReady = async () => {
            await callToSetClientList();
        };

        onReady();
    }, [])

     useEffect(() => {
        const onReady = async () => {
            await callToSetVehiclesList();
        };

        onReady();
    }, [clientId])

    async function callToSetVehiclesList() {
        if (clientId) {
            const parameters = { clientId: clientId }
            const vehicleIds = await ExecuteAxios.api('get', `/client-vehicle/lookup/`, { params: parameters });
            setVehiclesList(vehicleIds);
        }
    }

    async function callToSetClientList() {
        const clientIds = await ExecuteAxios.api('get', `/client/lookup/`);
        setClientList(clientIds);
    }

    const vehicleOptions = OptionsDataByAttributePath(vehicleList, ['description'], null, true);
    const clientOptions = OptionsDataByAttributePath(clientList, ['description'], null, true);

    const attributeList = [
        { name: "id", value: id, setter: setId },
        {
            name: "clientId",
            value: clientId,
            setter: setClientId,
            comboboxOptions: clientOptions
        },

        {
            name: "vehicleId",
            value: vehicleId,
            setter: setVehicleId,
            comboboxOptions: vehicleOptions
        },

        {
            name: "dateTime",
            value: dateTime,
            setter: setDateTime
        },

        {
            name: "observation",
            value: observation,
            setter: setObservation
        },

        {
            name: "isActive",
            value: isActive,
            setter: setIsActive,
            resetValue: 'S',
            checks: [
                {
                    field: 'isActive',
                    condition: 'VALUE_EQUALS',
                    arrayValue: ['S']
                }
            ]
        }
    ];

    useEffect(() => {
        reloadListItem();
    }, []);

    useEffect(() => {
        reloadListItem();
    }, [isActiveFilter, clientIdFilter]);

    useEffect(() => {
        setInserting(false);
        setUpdating(false);
        onUpdateServiceOrderId([]);
    }, [isActiveFilter]);

    useEffect(() => {
        reloadListItem();

        if ((!serviceOrderIdSelected || serviceOrderIdSelected.length !== 1) && updating) {
            setUpdating(false);
        }

        if (!serviceOrderIdSelected || serviceOrderIdSelected.length > 1) {
            onUpdateServiceOrderId([]);
        }

    }, [serviceOrderIdSelected, updating]);

    useEffect(() => {
        if (serviceOrderIdSelected && serviceOrderIdSelected.length === 1) {
            fetchDataDetail(serviceOrderIdSelected[0]);
        } else {
            fetchDataDetail(null);
        }
    }, [serviceOrderIdSelected]);

    async function reloadListItem() {
        const items = await ExecuteAxios.api(
            'get',
            `/service-order/grid/`,
            { params: { isActive: isActiveFilter, clientId: clientIdFilter || 0 } }
        );

        setRecordsItemByUserId(items);

        RemoveIdsNotFound.remove(
            items,
            onUpdateServiceOrderId,
            serviceOrderIdSelected
        );
    }

    const fetchDataDetail = async (serviceOrderId) => {

        const item = await ExecuteAxios.api(
            'get',
            `/service-order/detail/`,
            { params: { id: serviceOrderId || 0 } }
        );

        if (item) {
            setAttributesDetailItemPanel(item);
            ApplyAttributes.applyAttributes(item, attributeList);
        }

        if (!serviceOrderId) {
            ResetVariables.resetVariables(attributeList);
        }
    };

    const getItemPanelListOrDetail = () => {
        if (!updating && !inserting) {
            return (
                <PanelListDefault
                    onUpdateIdSelected={onUpdateServiceOrderId}
                    idsSelected={serviceOrderIdSelected}
                    data={recordsItemByUserId}
                    attributeList={attributeList}
                />
            );
        }

        return (
            <AutomaticallyListedAttributes
                list={attributesDetailItemPanel}
                attributeList={attributeList}
                readOnlyAttributes={readOnlyAttributes}
            />
        );
    };

    async function postItem() {
        try {
            setLoadingItemPanel(true);

            const response = await ExecuteAxios.api(
                'post',
                `/service-order/`,
                {
                    clientId,
                    vehicleId,
                    dateTime,
                    observation,
                    isActive
                }
            );

            if (response) {
                await reloadListItem();
                setInserting(false);
            }

            return response;

        } finally {
            setLoadingItemPanel(false);
        }
    }

    async function putItem() {
        try {
            setLoadingItemPanel(true);

            const response = await ExecuteAxios.api(
                'put',
                `/service-order/`,
                {
                    id,
                    clientId,
                    vehicleId,
                    dateTime,
                    observation,
                    isActive
                }
            );

            if (response) {
                await reloadListItem();
                setUpdating(false);
            }

            return response;

        } finally {
            setLoadingItemPanel(false);
        }
    }

    return (
        <Panel
            className={'main-modal'}
            style={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: 'calc(50% - 15px)',
            }}
        >
            <LoadingIndicator loaded={!loadingItemPanel}>
                <FilterOrder
                    isActiveFilter={isActiveFilter}
                    onUpdatesIsActiveFilter={setIsActiveFilter}
                    clientIdFilter={clientIdFilter}
                    setClientIdFilter={setClientIdFilter}
                />

                <Panel
                    titlePage={'Ordem'}
                    setUpdating={setUpdating}
                    setInserting={setInserting}
                    inserting={inserting}
                    updating={updating}
                    enableEdit
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                    canEdit={serviceOrderIdSelected?.length === 1}
                    putFunction={putItem}
                    enableNewRecord
                    canNewRecord
                    postFunction={postItem}
                    canViewCRUD
                    recordsSelected={serviceOrderIdSelected}
                    viewOnlyOneRecordCRUD
                    resetVariables={() =>
                        ResetVariables.resetVariables(attributeList)
                    }
                    dataListedAttributes={attributesDetailItemPanel}
                    attributeList={attributeList}
                    onUpdateIdSelected={onUpdateServiceOrderId}
                >
                    {getItemPanelListOrDetail()}
                </Panel>
            </LoadingIndicator>
        </Panel>
    );
};

export default ServiceOrder;
